/* eslint-disable jsx-a11y/anchor-is-valid */
import axios from 'axios'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import { useAuth } from '../../modules/auth'
// import PreviewPDFModal from './PreviewPDFModal'
import { Form, Spinner } from 'react-bootstrap'
import { FaBed, FaCheckCircle, FaExclamationCircle, FaHospital, FaNotesMedical, FaStethoscope, FaTimes } from 'react-icons/fa'
import 'react-toastify/dist/ReactToastify.css'
import styled, { css, keyframes } from 'styled-components'
import CustomCalendar from '../../../_metronic/layout/calendar'

// Custom number formatting function
export const formatNumberWithSeparator = (value: number, options?: Intl.NumberFormatOptions) => {
  // Floor the value before formatting
  const flooredValue = Math.floor(value * 10000) / 10000

  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
    ...options,
  }).format(flooredValue)
}

type UploadAreaProps = React.HTMLAttributes<HTMLDivElement> & {
  $disabled?: boolean
}

type ProgressBarProps = React.HTMLAttributes<HTMLDivElement> & {
  $progress: number
}

type ValidationMessageProps = React.HTMLAttributes<HTMLDivElement> & {
  $isError?: boolean
}

const Tooltip = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`

const ValidationMessage = styled.div<ValidationMessageProps>`
  ${(props) => css`
    color: ${props.$isError ? '#dc3545' : '#28a745'};
    font-size: 0.875em;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
  `}
`

const IcdDropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`

const IcdName = styled.div`
  font-weight: 500;
  color: #2c3e50;
`

const IcdCode = styled.div`
  font-size: 0.875em;
  color: #6c757d;
`

const TooltipText = styled.div`
  visibility: hidden;
  opacity: 0;
  background-color: #2c3e50;
  color: white;
  text-align: center;
  padding: 8px 12px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.3s ease;
  white-space: nowrap;
  font-size: 0.875em;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #2c3e50 transparent transparent transparent;
  }
`

const StyledLabel = styled.label`
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #059bb6;
  }
`

const StyledSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.3s ease;

  &:hover {
    background: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`

const StyledInput = styled(Form.Control)`
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #059bb6;
    box-shadow: 0 0 0 3px rgba(5, 155, 182, 0.1);
  }

  &:hover {
    border-color: ${(props) => (props.readOnly ? '#e9ecef' : '#059bb6')};
    cursor: ${(props) => (props.readOnly ? 'not-allowed' : 'text')};
  }

  &[readOnly] {
    background-color: #f5f5f5;
    color: #6c757d;
  }
`

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`

const ErrorInput = styled(StyledInput)`
  &.error {
    border-color: #dc3545;
    animation: ${shake} 0.5s ease;
  }
`

const ProgressBar = styled.div<ProgressBarProps>`
  ${(props) => css`
    width: ${props.$progress}%;
    height: 8px;
    background: linear-gradient(90deg, #059bb6 0%, #05b6a6 100%);
    border-radius: 4px;
    transition: width 0.3s ease;
  `}
`

interface OutpatientPageProps {
  claimDetails?: any
  onSubmit: (e: React.FormEvent, type: 'save' | 'submit') => void
  formData?: any
  onFormDataChange?: (data: any) => void
  onDefaultDocumentsReady?: (docs: any[]) => void
  setShowConfirmation?: (show: boolean) => void
  setIsSaveClaim?: (isSaveClaim: boolean) => void
}

type DocumentFile = {
  id: any
  name: string
  size: number
  status: 'IDLE' | 'INVALID' | 'Request Document' | 'VALID'
}

type DocumentData = {
  label: string
  document: DocumentFile[]
}

const OutpatientPage = forwardRef((props: OutpatientPageProps, ref) => {
  const {
    claimDetails,
    onSubmit,
    onDefaultDocumentsReady,
    formData,
    onFormDataChange,
    setShowConfirmation,
    setIsSaveClaim,
  } = props
  const user = useAuth()
  const { auth } = useAuth()
  const navigate = useNavigate()
  const jsonString = claimDetails?.detailBenefit
  const parsedJson = jsonString ? JSON.parse(jsonString) : {}
  // console.log("parsedJson", parsedJson);
  const parsedMemberDetail = claimDetails?.memberDetail
    ? JSON.parse(claimDetails?.memberDetail)
    : {}
  const [diagnosis, setDiagnosis] = useState(formData?.diagnosis || '')
  const [icdData, setIcdData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIcd, setSelectedIcd] = useState<{
    code: string
    name: string
  } | null>(null)
  const [supportingDiagnosis, setSupportingDiagnosis] = useState<string[]>(
    formData?.supportingDiagnosis || Array(3).fill('')
  )
  const [supportingIcdData, setSupportingIcdData] = useState<any[][]>(Array(3).fill([]))
  const [selectedSupportingIcds, setSelectedSupportingIcds] = useState<
    ({ code: string; name: string } | null)[]
  >(formData?.selectedSupportingIcds || Array(3).fill(null))
  const [doctor, setDoctor] = useState(formData?.doctor || '')
  const [prescription, setPrescription] = useState(formData?.prescription || '')
  const [department, setDepartment] = useState(formData?.department || '')
  const [notes, setNotes] = useState(formData?.notes || '')
  // const [specialTerms, setSpecialTerms] = useState(formData?.specialTerms || '')
  // const [totalBiaya, setTotalBiaya] = useState(0)
  // const [totalExcess, setTotalExcess] = useState(0)
  // const [totalCover, setTotalCover] = useState(0)
  const [fglDocumentUrl, setFglDocumentUrl] = useState<string | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false)
  const [errors, setErrors] = useState({
    diagnosis: false,
    doctor: false,
    department: false,
    // notes: false,
  })

  const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({})
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({})
  // const [files, setFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [successMessage, setSuccessMessage] = useState<string>('')

  const mainDiagnosisDropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis1DropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis2DropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis3DropdownRef = useRef<HTMLDivElement>(null)
  const [isMainDiagnosisDropdownVisible, setIsMainDiagnosisDropdownVisible] = useState(false)
  const [isSupportingDiagnosis1DropdownVisible, setIsSupportingDiagnosis1DropdownVisible] =
    useState(false)
  const [isSupportingDiagnosis2DropdownVisible, setIsSupportingDiagnosis2DropdownVisible] =
    useState(false)
  const [isSupportingDiagnosis3DropdownVisible, setIsSupportingDiagnosis3DropdownVisible] =
    useState(false)

  const defaultDocumentsOutpatient = [
    { label: 'Surat Keterangan Dokter Rawat Jalan (RJ)', key: 0 },
    { label: 'Surat Pernyataan', key: 1 },
    { label: 'Copy Kartu Asuransi Pasien', key: 2 },
    { label: 'Copy KTP Pasien', key: 3 },
    { label: 'Surat Billing Akhir', key: 4 },
    { label: 'Surat Hasil Penunjang', key: 5 },
  ]

  const [documentsData, setDocumentsData] = useState<DocumentData[]>(() =>
    defaultDocumentsOutpatient.map((doc) => ({
      label: doc.label,
      document: [],
    }))
  )

  const claimData = claimDetails?.claimData ? JSON.parse(claimDetails?.claimData) : null
  const isReadOnly = !['Pre-Registered', 'Registered', 'Pre-Auth', 'Pending on FGL'].includes(
    claimDetails?.status || ''
  )
  const [costs, setCosts] = useState<Record<string, number>>(formData?.costs || {})
  const [excess, setExcess] = useState<Record<string, number>>(formData?.excess || {})
  const [coShare, setCoShare] = useState<Record<string, number>>({})
  const [isProcessClaim, setIsProcessClaim] = useState(false)

  const planCode = parsedJson?.PlanCode

  const [productCode, setProductCode] = useState<string>('')
  const [outdatedDate, setOutdatedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [renderDocuments, setRenderDocuments] = useState<any[]>([])

  useEffect(() => {
    if (onDefaultDocumentsReady) {
      onDefaultDocumentsReady(defaultDocumentsOutpatient)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mainDiagnosisDropdownRef.current &&
        !mainDiagnosisDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMainDiagnosisDropdownVisible(false)
      }
      if (
        supportingDiagnosis1DropdownRef.current &&
        !supportingDiagnosis1DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis1DropdownVisible(false)
      }
      if (
        supportingDiagnosis2DropdownRef.current &&
        !supportingDiagnosis2DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis2DropdownVisible(false)
      }
      if (
        supportingDiagnosis3DropdownRef.current &&
        !supportingDiagnosis3DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis3DropdownVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!documentsData || !claimDetails) return

    const defaultLabels = defaultDocumentsOutpatient.map(d => d.label)

    const mergedDocuments = [
      ...defaultDocumentsOutpatient,
      ...documentsData.filter(doc => {
        const isRequestDocument = !defaultLabels.includes(doc.label)

        if (!isRequestDocument) return false

        if (doc.document && doc.document.length > 0) return true

        return doc.document?.some(
          f => f.status === 'Request Document' || f.status === 'INVALID'
        )
      })
    ]

    setRenderDocuments(mergedDocuments)
  }, [documentsData, defaultDocumentsOutpatient])


  const fetchDocuments = async () => {
    const claimNumber = claimDetails?.claimNumber
    // Use the apiEndpoint from claimDetails if provided, otherwise use the default endpoint
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Discharge`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    try {
      const response = await axios.get(apiUrl, { headers })
      if (response.data.isSuccess) {
        const apiDocuments = response.data.data.filter((doc: any) => doc.label !== 'Prescription')

        // Check for invalid documents across all documents
        const hasInvalidDocuments = apiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some((file: any) => file.status === 'INVALID' || file.status === 'Request Document')
        )

        // Create a map of all unique documents
        const allDocuments = new Map()

        // Add default documents first
        defaultDocumentsOutpatient.forEach((doc) => {
          allDocuments.set(doc.label, { ...doc, key: doc.key })
        })

        // Add or update with API documents
        apiDocuments.forEach((doc: any, index: number) => {
          if (!allDocuments.has(doc.label)) {
            allDocuments.set(doc.label, {
              label: doc.label,
              key: defaultDocumentsOutpatient.length + index,
              document: doc.document,
            })
          } else {
            const existing = allDocuments.get(doc.label)
            allDocuments.set(doc.label, {
              ...existing,
              document: doc.document,
            })
          }
        })

        console.log("API DOCUMENTT", apiDocuments);

        // Convert map back to array
        const finalDocuments = Array.from(allDocuments.values())
        console.log('finalDocuments: ', finalDocuments)

        // Create updated files object
        const updatedFiles: Record<string, any[]> = {}
        finalDocuments.forEach((doc: any) => {
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name.split('.').pop(),
              status: file.status,
              isArchive: file.isArchive,
            }))
          } else {
            updatedFiles[doc.label] = []
          }
        })

        setDocumentsData(finalDocuments)
        console.log("Final document", finalDocuments);
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to fetch documents. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    }
  }

  const fetchMemberDetail = async () => {
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Data/Provider/GetMemberByCard?ProviderCode=${claimDetails?.providerCode}&CardNo=${claimDetails?.cardNumber}&Dob=${parsedMemberDetail?.DateOfBirth}`
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      setLoading(true)
      const response = await axios.get(apiUrl, { headers })
      if (response.data.isSuccess) {
        setProductCode(response.data.data.productCode)
      }
    } catch (err) {
      console.error('Error fetching member detail:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDocuments()
    // fetchMemberDetail()
  }, [])

  useEffect(() => {
    if (claimDetails?.status !== 'FGL Released') {
      fetchMemberDetail()
    }
  }, [claimDetails?.status !== 'FGL Released'])

  const fetchIcdData = async (searchTerm: string, index: number) => {
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${parsedJson.BenefitCode}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      setLoading(true)
      const response = await axios.get(apiUrl, { headers })
      if (index === -1) {
        setIcdData(response.data.data)
        setIsMainDiagnosisDropdownVisible(true)
      } else {
        const newSupportingIcdData = [...supportingIcdData]
        newSupportingIcdData[index] = response.data.data
        setSupportingIcdData(newSupportingIcdData)
      }
    } catch (err) {
      setError('Error fetching ICD-10 data')
    } finally {
      setLoading(false)
    }
  }

  const fetchIcdDetails = async (icdCode: string) => {
    if (!icdCode) return null
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${icdCode}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${parsedJson.BenefitCode}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      const response = await axios.get(apiUrl, { headers })
      const matchingIcd = response.data.data.find((icd: any) => icd.code === icdCode)
      return matchingIcd ? `${matchingIcd.name} (${matchingIcd.code})` : icdCode
    } catch (err) {
      console.error('Error fetching ICD details:', err)
      return icdCode
    }
  }

  useEffect(() => {
    const loadExistingData = async () => {
      if (claimData && !diagnosis) {
        // Only load if claimData exists and diagnosis is empty
        // Load ICD codes
        if (claimData.IcdCode1) {
          const icdDetails = await fetchIcdDetails(claimData.IcdCode1)
          setDiagnosis(icdDetails || '')
        }

        // Set supporting diagnoses directly from claimData
        const supportingDiagnosesData = [
          claimData.IcdCode2 ? `${claimData.IcdDesc2 || ''} (${claimData.IcdCode2})` : '',
          claimData.IcdCode3 ? `${claimData.IcdDesc3 || ''} (${claimData.IcdCode3})` : '',
          claimData.IcdCode4 ? `${claimData.IcdDesc4 || ''} (${claimData.IcdCode4})` : '',
        ]

        // Update supporting diagnoses state
        setSupportingDiagnosis(supportingDiagnosesData)

        // Update selected supporting ICDs state with proper null handling
        const supportingIcds = [
          claimData.IcdCode2 ? { code: claimData.IcdCode2, name: claimData.IcdDesc2 || '' } : null,
          claimData.IcdCode3 ? { code: claimData.IcdCode3, name: claimData.IcdDesc3 || '' } : null,
          claimData.IcdCode4 ? { code: claimData.IcdCode4, name: claimData.IcdDesc4 || '' } : null,
        ] as ({ code: string; name: string } | null)[]

        setSelectedSupportingIcds(supportingIcds)

        // Load other form data only if empty
        if (!doctor) setDoctor(claimData.Doctor || '')
        if (!department) setDepartment(claimData.PolyName || '')
        if (!notes) setNotes(claimData.Remark || '')
        if (!prescription) setPrescription(claimData.Prescription || '')
      }
    }

    loadExistingData()
  }, []) // Only depend on claimData changes

  // Prevent unnecessary updates from form data that could cause flickering
  useEffect(() => {
    // Skip if there's no form data or it's empty
    if (!formData || Object.keys(formData).length === 0) {
      return
    }

    // Check if we're in the middle of user input to prevent flickering
    const userIsTyping = document.activeElement && document.activeElement.tagName === 'INPUT'

    // Don't update fields that are currently being edited
    if (
      userIsTyping &&
      document.activeElement &&
      (document.activeElement.id === 'diagnosis-input' ||
        document.activeElement.id === 'supporting-diagnosis-0' ||
        document.activeElement.id === 'supporting-diagnosis-1' ||
        document.activeElement.id === 'supporting-diagnosis-2')
    ) {
      return
    }

    const shouldUpdate =
      formData.diagnosis !== diagnosis ||
      formData.doctor !== doctor ||
      formData.department !== department ||
      formData.notes !== notes ||
      formData.prescription !== prescription ||
      JSON.stringify(formData.supportingDiagnosis) !== JSON.stringify(supportingDiagnosis)

    if (shouldUpdate) {
      // Batch these updates together to prevent multiple renders
      const updates = () => {
        setDiagnosis(formData.diagnosis || diagnosis)
        setDoctor(formData.doctor || doctor)
        setDepartment(formData.department || department)
        setNotes(formData.notes || notes)
        setPrescription(formData.prescription || prescription)
        setSupportingDiagnosis(formData.supportingDiagnosis || supportingDiagnosis)
        setSelectedSupportingIcds(formData.selectedSupportingIcds || selectedSupportingIcds)
        setCosts(formData.costs || costs)
        setExcess(formData.excess || excess)
        // setTotalBiaya(formData.totalBiaya || totalBiaya)
      }

      // Execute updates in next tick to avoid conflict with ongoing render
      setTimeout(updates, 0)
    }
  }, [formData]) // Only depend on formData changes

  // Add debounce helper function to prevent excessive API calls
  const useDebounce = (func, delay) => {
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    return (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        func(...args)
        timeoutRef.current = null
      }, delay)
    }
  }

  // Debounced fetch function to reduce flickering
  const debouncedFetchIcdData = useDebounce((searchTerm, index) => {
    if (searchTerm.length > 2) {
      fetchIcdData(searchTerm, index)
    } else if (index === -1) {
      setIcdData([])
    } else {
      setSupportingIcdData((prev) => {
        const newData = [...prev]
        newData[index] = []
        return newData
      })
    }
  }, 300)

  const handleDiagnosisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDiagnosis(value)
    debouncedFetchIcdData(value, -1)
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const value = e.target.value
    setSupportingDiagnosis((prev) => {
      const newDiagnoses = [...prev]
      newDiagnoses[index] = value
      return newDiagnoses
    })

    if (value.trim() === '') {
      setSelectedSupportingIcds((prev) => {
        const next = [...prev]
        next[index] = null
        return next
      })
    }

    setIsSupportingDiagnosis1DropdownVisible(true)
    setIsSupportingDiagnosis2DropdownVisible(true)
    setIsSupportingDiagnosis3DropdownVisible(true)
    debouncedFetchIcdData(value, index)
  }

  const handleSelectIcd = (icd: { code: string; name: string }) => {
    // Use batch updates to prevent flickering
    const formattedDiagnosis = `${icd.name} (${icd.code})`

    // Update everything in a single render cycle
    setSelectedIcd(icd)
    setDiagnosis(formattedDiagnosis)
    setIcdData([]) // Clear options after selection
  }

  const handleSelectSupportingIcd = (icd: { code: string; name: string }, index: number) => {
    // Create all updates before setting state to reduce render cycles
    const newSelectedIcds = [...selectedSupportingIcds]
    newSelectedIcds[index] = icd

    const formattedDiagnosis = `${icd.name} (${icd.code})`

    // Update all states at once - React will batch these updates
    setSelectedSupportingIcds(newSelectedIcds)
    setSupportingDiagnosis((prev) => {
      const newDiagnoses = [...prev]
      newDiagnoses[index] = formattedDiagnosis // Use formatted string instead of just code
      return newDiagnoses
    })
    setSupportingIcdData((prev) => {
      const newData = [...prev]
      newData[index] = []
      return newData
    })
  }

  const validateFields = () => {
    console.log('Current Values:', {
      diagnosis,
      doctor,
      department,
      //   notes,
    })

    const newErrors = {
      diagnosis: !diagnosis,
      doctor: !doctor,
      department: !department,
      //   notes: !notes,
    }
    setErrors(newErrors)
    return Object.values(newErrors).some(Boolean)
  }

  const handleSubmit = async (e: React.FormEvent, type: 'save' | 'submit') => {
    e.preventDefault()
    if (validateFields()) {
      throw new Error('Harap lengkapi semua field yang wajib diisi')
      return
    }

    var detailMember = JSON.parse(claimDetails?.memberDetail)

    setLoading(true)
    setIsProcessClaim(true)
    setShowConfirmation && setShowConfirmation(false)

    // Extract ICD code from the diagnosis string
    const extractIcdCode = (diagnosisString: string) => {
      const match = diagnosisString.match(/\((.*?)\)/)
      return match ? match[1] : null
    }

    const finalData = {
      claimNo: claimDetails?.claimNumber,
      // Main diagnosis (ICD1)
      icdCode1: selectedIcd?.code || extractIcdCode(diagnosis) || null,
      // Supporting diagnoses (ICD2, ICD3, ICD4)
      icdCode2: selectedSupportingIcds[0]?.code || extractIcdCode(supportingDiagnosis[0]) || null,
      icdCode3: selectedSupportingIcds[1]?.code || extractIcdCode(supportingDiagnosis[1]) || null,
      icdCode4: selectedSupportingIcds[2]?.code || extractIcdCode(supportingDiagnosis[2]) || null,
      doctor,
      polyCode: department,
      polyName: department,
      remark: notes,
      updatedBy: user.currentUser?.email,
      BenefitDetail: {
        id: parsedJson.Id,
        benefitName: parsedJson.BenefitName,
        planName: parsedJson.PlanName,
        benefitCode: parsedJson.BenefitCode,
        condition: parsedJson.Condition,
        annualLimit: parsedJson.AnnualLimit,
        subBenefits: parsedJson.SubBenefits.map((subBenefit: any) => ({
          id: subBenefit.Id,
          category: 'OUTPATIENT',
          SubBenefitName: subBenefit.SubBenefitName,
          isCashless: subBenefit.IsCashless,
          isReimburstement: subBenefit.IsReimburstement,
          annualLimit: subBenefit.Amount,
          type: 'OUTPATIENT',
          claimValue: Number(costs[subBenefit.SubBenefitName]),
          excessValue: Number(excess[subBenefit.SubBenefitName]),
          details: subBenefit.Details,
          condition: subBenefit?.Condition || [],
          coShare: coShare[subBenefit.SubBenefitName] || 0,
        })),
      },
    }

    // Log the ICD codes for verification
    console.log('Submitting ICD codes:', {
      icdCode1: finalData.icdCode1,
      icdCode2: finalData.icdCode2,
      icdCode3: finalData.icdCode3,
      icdCode4: finalData.icdCode4,
    })

    // Remove null or undefined ICD codes
    Object.keys(finalData).forEach((key) => {
      if (key.startsWith('icdCode') && !finalData[key]) {
        delete finalData[key]
      }
    })

    console.log('Final Data to Submit: ', JSON.stringify(finalData, null, 2))

    try {
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      // if (process.env.REACT_APP_API_PROCESS_CLAIM_URL == undefined) {
      const response = await axios.post(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/ProcessClaim/OUTPATIENT`,
        finalData,
        { headers }
      )

      if (response.data.isSuccess === true) {
        // Store current form state
        const currentFormState = {
          diagnosis,
          doctor,
          department,
          notes,
          prescription,
          supportingDiagnosis,
          selectedSupportingIcds,
          costs,
          // totalBiaya,
        }

        // Update parent component with current form state
        if (onFormDataChange) {
          onFormDataChange(currentFormState)
        }

        if (type === 'save') {
          setIsSaveClaim && setIsSaveClaim(true)
          toast.success('Data berhasil disimpan!', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
        }

        if (type === 'submit') {
          const statusChangeResponse = await fetch(
            `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UpdateStatus`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
              },
              body: JSON.stringify({
                claimNumber: claimDetails?.claimNumber,
                status: 'Request FGL',
                username: user.currentUser?.email,
                source: 'Provider',
                emailProvider: claimDetails?.createdBy,
                treatmentDate: claimDetails?.indate,
                djp: doctor,
                treatmentPlan: '',
                costEstimation: '',
                currency: '',
                planPolis: detailMember.PlanName,
                memberName: claimDetails?.memberName,
                diagnose: diagnosis,
                noPolis: claimDetails?.policyNumber,
                nameProvider: claimDetails?.providerName,
                insuranceName: claimDetails?.customerGroup,
                remarkClaim: notes,
                dischargeDate: outdatedDate,
              }),
            }
          )

          if (statusChangeResponse.ok) {
            toast.success('Claim already success created', {
              position: 'top-right',
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: 'colored',
              style: {
                background: '#4CAF50',
                color: 'white',
                fontSize: '16px',
                padding: '16px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              },
              progressStyle: {
                background: 'rgba(255,255,255,0.7)',
              },
              onClose: () => {
                navigate('/builder')
              },
            })
          } else if (statusChangeResponse.status === 500) {
            toast.error('Failed to update status. Please try again later.', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: 'colored',
            })
          } else {
            toast.error('Failed to update status. Please try again later.', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: 'colored',
            })
          }
        }
        setLoading(false)
        setIsProcessClaim(false)
      } else {
        // Handle unexpected response
        toast.error('Failed to update status. Please try again later.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'colored',
        })
      }
      // }
    } catch (error) {
      console.error('Error submitting data:', error)
      // Add error toast
      toast.error('Failed to process claim. Please try again.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      })
      setLoading(false)
      setIsProcessClaim(false)
      throw error
    } finally {
      setLoading(false)
      setIsProcessClaim(false)
    }
  }

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }))

  // Update parent component when form fields change
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        diagnosis,
        doctor,
        prescription,
        department,
        notes,
        // specialTerms,
        costs,
        // totalBiaya,
        supportingDiagnosis,
        selectedSupportingIcds,
        documentsData,
      })
    }
  }, [
    diagnosis,
    doctor,
    prescription,
    department,
    notes,
    // specialTerms,
    costs,
    // totalBiaya,
    supportingDiagnosis,
    selectedSupportingIcds,
    documentsData,
  ])

  const handleDeleteInvalidFileById = async (documentId: number) => {
    try {
      const claimNumber = claimDetails?.claimNumber

      if (!claimNumber || !documentId) return

      setDeletingFiles((prev) => ({ ...prev, [documentId]: true }))

      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${documentId}&claimNumber=${claimNumber}`

      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      const response = await axios.post(apiUrl, {}, { headers })

      if (response.data === 'DELETED FILE SUCCESS.' || response.data.isSuccess) {
        const listResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Discharge`,
          { headers }
        )

        if (listResponse.data.isSuccess) {
          setDocumentsData(listResponse.data.data)
        }
      } else {
        throw new Error('Delete failed')
      }
    } catch (error) {
      console.error('Delete invalid document error:', error)
      toast.error('Failed to remove invalid document', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    } finally {
      setDeletingFiles({})
    }
  }

  const handleOutpatientFileChange = async (label: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files)
      try {
        await uploadOutpatientFiles(label, selectedFiles)
      } catch (error) {
        console.error('Error handling file change:', error)
        toast.error('Failed to upload file. Please try again.', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      }
    }
  }

  const uploadOutpatientFiles = async (label: string, files: File[]) => {
    if (!files || files.length === 0) return
    const claimNumber = claimDetails?.claimNumber
    const claimStatus = claimDetails?.status

    const isPendingFGL = claimStatus === 'Pending on FGL'

    const currentDoc = documentsData.find((doc) => doc.label === label)

    const invalidDoc = currentDoc?.document?.find(
      (doc) => doc.status === 'INVALID' || doc.status === 'Request Document'
    )

    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    const typeDocument = label

    const queryParams = new URLSearchParams({
      claimNumber: claimNumber || '',
      typeDocument: typeDocument,
      user: user.currentUser?.email || '',
      // status: 'VALID',
      bucket: 'Discharge',
      id: '0',
    })

    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UploadFiles?${queryParams.toString()}`

    try {
      setIsUploading((prev) => ({ ...prev, [label]: true }))
      setUploadProgress(0)

      if (isPendingFGL && invalidDoc) {
        // Delete the invalid document before uploading new files
        await handleDeleteInvalidFileById(invalidDoc.id)
      }

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${user.currentUser?.token}`,
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const current = progressEvent.loaded
          const percentage = Math.round((current / total) * 100)
          setUploadProgress(percentage)
        },
      })

      if (response.data.isSuccess) {
        // Update UI immediately with uploaded file info from response
        if (response.data.data && response.data.data.fileName) {
          setDocumentsData((prevData) =>
            prevData.map((doc) => {
              if (doc.label === label) {
                return {
                  ...doc,
                  document: [
                    ...(doc.document || []),
                    ...files.map((file) => ({
                      id: response.data.data.id || Date.now(),
                      name: file.name,
                      size: file.size,
                      status: 'IDLE' as const,
                    })),
                  ],
                }
              }
              return doc
            })
          )
        }

        console.log('respon', response.data);


        setSuccessMessage('File uploaded successfully!')
        setTimeout(() => {
          setSuccessMessage('')
        }, 3000)
        await fetchDocuments()
      } else {
        throw new Error(response.data.message || 'Upload failed')
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      toast.error('Failed to upload file. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    } finally {
      setIsUploading((prev) => ({ ...prev, [label]: false }))
      setUploadProgress(0)
    }
  }

  const handleDeleteFile = async (docKey: number, fileIndex: number) => {
    try {
      const claimNumber = claimDetails?.claimNumber
      const docLabel = defaultDocumentsOutpatient.find((doc) => doc.key === docKey)?.label

      if (!docLabel) {
        toast.error('Document not found', {
          position: 'bottom-right',
          autoClose: 3000,
        })
        return
      }

      const currentDoc = documentsData.find((doc) => doc.label === docLabel)
      const fileToDelete = currentDoc?.document?.[fileIndex]

      if (!fileToDelete) {
        toast.error('File not found', {
          position: 'bottom-right',
          autoClose: 3000,
        })
        return
      }

      setDeletingFiles((prev) => ({ ...prev, [fileToDelete.id]: true }))

      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${fileToDelete.id}&claimNumber=${claimNumber}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      const response = await axios.post(apiUrl, {}, { headers })

      if (response.data === 'DELETED FILE SUCCESS.' || response.data.isSuccess) {
        // Refresh document list
        const listResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Discharge`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listResponse.data.isSuccess) {
          setDocumentsData(listResponse.data.data)
        }

        setSuccessMessage('File deleted successfully!')
        setTimeout(() => {
          setSuccessMessage('')
        }, 3000)
      } else {
        throw new Error('Delete operation failed')
      }
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Error deleting file. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    } finally {
      setDeletingFiles({})
    }
  }

  // Add the following useEffect after all other useEffects
  useEffect(() => {
    // When fglDocumentUrl is set but we're still loading,
    // create a hidden iframe to load the document
    if (fglDocumentUrl && isLoadingPreview) {
      // The actual change happens when the iframe loads in the modal
      console.log('FGL document URL is set, loading document...')
    }
  }, [fglDocumentUrl, isLoadingPreview])

  const getDocumentStatusIcon = (document: any) => {
    if (!document || !document.id) {
      return (
        <div style={{ fontSize: '16px', color: '#FD7E14', background: 'rgba(253, 126, 20, 0.1)', padding: '6px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
          <FaExclamationCircle />
        </div>
      )
    }

    if (document.status === 'VALID') {
      return (
        <div style={{ fontSize: '16px', color: '#20C997', background: 'rgba(32, 201, 151, 0.1)', padding: '6px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
          <FaCheckCircle />
        </div>
      )
    }

    if (document.status === 'INVALID' || document.status === 'Request Document') {
      return (
        <div style={{ fontSize: '16px', color: '#FA5252', background: 'rgba(250, 82, 82, 0.1)', padding: '6px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
          <FaExclamationCircle />
        </div>
      )
    }

    return (
      <div style={{ fontSize: '16px', color: '#FD7E14', background: 'rgba(253, 126, 20, 0.1)', padding: '6px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
        <FaExclamationCircle />
      </div>
    )
  }

  const getDocumentStatusText = (document: any) => {
    if (!document) {
      return 'Document not uploaded'
    }

    if (document.status === 'VALID') {
      return 'Approved'
    }

    if (document.status === 'INVALID') {
      return 'Revision needed'
    }

    if (document.status === 'Request Document') {
      return 'Request Document'
    }

    return 'Uploaded'
  }

  const handleDischargeDate = (date: string) => {
    setOutdatedDate(date)
  }

  return (
    <>
      <ToastContainer
        position='top-right'
        autoClose={8000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
        style={{ zIndex: 9999 }} // Ensure toast appears above modal
      />

      {(loading || isProcessClaim) && (
        <div
          className='loading-indicator'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000,
          }}
        >
          <Spinner animation='border' />
          <span style={{ marginLeft: '10px' }}>
            {isProcessClaim
              ? 'Processing Claim...'
              : 'Loading...'}
          </span>
        </div>
      )}

      {/* Left Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {/* Section 1: Medical Information */}
        <div className='bg-white rounded-lg' style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '16px' }}>
          <h3 className='text-lg font-semibold mb-4' style={{ color: '#059BB6', marginBottom: '16px' }}>Medical Information</h3>
          <form style={{ display: 'flex', flexDirection: 'column' }}>
            {/* INPUT DATE */}
            <StyledSection>
              <CustomCalendar
                label={
                  <Tooltip>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaBed style={{ color: '#059bb6' }} />
                      Tanggal Keluar
                    </span>
                    <TooltipText className='tooltip-text'>
                      Tanggal keluar perawatan pasien
                    </TooltipText>
                  </Tooltip>
                }
                value={outdatedDate}
                onChange={handleDischargeDate}
                disabled={isReadOnly}
              />
            </StyledSection>

            {/* Diagnosa Utama */}
            <StyledSection>
              <Tooltip>
                <label className='form-label'>
                  <StyledLabel>
                    <FaStethoscope />
                    Diagnosa Utama (ICD 10) <span style={{ color: '#059BB6' }}> *</span>
                  </StyledLabel>
                  <TooltipText className='tooltip-text'>
                    Required field - Enter the main diagnosis ICD code
                  </TooltipText>
                </label>
              </Tooltip>
              <div style={{ position: 'relative' }}>
                <ErrorInput
                  type='text'
                  value={diagnosis}
                  onChange={handleDiagnosisChange}
                  placeholder='Kode deskripsi/deskripsi ICD 10'
                  readOnly={isReadOnly}
                  className={diagnosis === '' && isReadOnly ? 'error' : ''}
                />
                {diagnosis === '' && isReadOnly && (
                  <ValidationMessage $isError>
                    <FaTimes /> Main diagnosis is required
                  </ValidationMessage>
                )}
                {isMainDiagnosisDropdownVisible && icdData.length > 0 && (
                  <div
                    ref={mainDiagnosisDropdownRef}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      background: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      width: '100%',
                    }}
                  >
                    {icdData.map((icd) => (
                      <IcdDropdownItem
                        key={icd.id}
                        onClick={() => {
                          handleSelectIcd({ code: icd.code, name: icd.name })
                          setIsMainDiagnosisDropdownVisible(false)
                        }}
                      >
                        <IcdName>{icd.name}</IcdName>
                        <IcdCode>{icd.code}</IcdCode>
                      </IcdDropdownItem>
                    ))}
                  </div>
                )}
              </div>
            </StyledSection>

            {/* Diagnosa Pendukung */}
            <StyledSection>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <Tooltip>
                    <label className='form-label'>
                      <StyledLabel>
                        <FaStethoscope />
                        ICD-10 2
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        Optional supporting diagnosis
                      </TooltipText>
                    </label>
                  </Tooltip>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <StyledInput
                      type='text'
                      value={supportingDiagnosis[0]}
                      onChange={(e) => handleInputChange(e, 0)}
                      placeholder='Kode deskripsi/deskripsi ICD 10'
                      readOnly={isReadOnly}
                    />
                    {isSupportingDiagnosis1DropdownVisible && supportingIcdData[0].length > 0 && (
                      <div
                        ref={supportingDiagnosis1DropdownRef}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          width: '100%',
                        }}
                      >
                        {supportingIcdData[0].map((icd) => (
                          <IcdDropdownItem
                            key={icd.id}
                            onClick={() => {
                              handleSelectSupportingIcd({ code: icd.code, name: icd.name }, 0)
                              setIsSupportingDiagnosis1DropdownVisible(false)
                            }}
                          >
                            <IcdName>{icd.name}</IcdName>
                            <IcdCode>{icd.code}</IcdCode>
                          </IcdDropdownItem>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Tooltip>
                    <label className='form-label'>
                      <StyledLabel>
                        <FaStethoscope />
                        ICD-10 3
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        Optional supporting diagnosis
                      </TooltipText>
                    </label>
                  </Tooltip>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <StyledInput
                      type='text'
                      value={supportingDiagnosis[1]}
                      onChange={(e) => handleInputChange(e, 1)}
                      placeholder='Kode deskripsi/deskripsi ICD 10'
                      readOnly={isReadOnly}
                    />
                    {isSupportingDiagnosis2DropdownVisible && supportingIcdData[1].length > 0 && (
                      <div
                        ref={supportingDiagnosis1DropdownRef}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          width: '100%',
                        }}
                      >
                        {supportingIcdData[1].map((icd) => (
                          <IcdDropdownItem
                            key={icd.id}
                            onClick={() => {
                              handleSelectSupportingIcd({ code: icd.code, name: icd.name }, 1)
                              setIsSupportingDiagnosis2DropdownVisible(false)
                            }}
                          >
                            <IcdName>{icd.name}</IcdName>
                            <IcdCode>{icd.code}</IcdCode>
                          </IcdDropdownItem>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Tooltip>
                    <label className='form-label'>
                      <StyledLabel>
                        <FaStethoscope />
                        ICD-10 4
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        Optional supporting diagnosis
                      </TooltipText>
                    </label>
                  </Tooltip>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <StyledInput
                      type='text'
                      value={supportingDiagnosis[2]}
                      onChange={(e) => handleInputChange(e, 2)}
                      placeholder='Kode deskripsi/deskripsi ICD 10'
                      readOnly={isReadOnly}
                    />
                    {isSupportingDiagnosis3DropdownVisible && supportingIcdData[2].length > 0 && (
                      <div
                        ref={supportingDiagnosis1DropdownRef}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          width: '100%',
                        }}
                      >
                        {supportingIcdData[2].map((icd) => (
                          <IcdDropdownItem
                            key={icd.id}
                            onClick={() => {
                              handleSelectSupportingIcd({ code: icd.code, name: icd.name }, 2)
                              setIsSupportingDiagnosis3DropdownVisible(false)
                            }}
                          >
                            <IcdName>{icd.name}</IcdName>
                            <IcdCode>{icd.code}</IcdCode>
                          </IcdDropdownItem>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </StyledSection>

            {/* Dokter & Poli */}
            <StyledSection>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className='form-label'>
                    <StyledLabel>
                      <FaHospital />
                      Dokter <span style={{ color: '#059BB6' }}> *</span>
                    </StyledLabel>
                  </label>
                  <input
                    type='text'
                    className={`form-control ${errors.doctor ? 'border-red-500' : ''}`}
                    value={doctor}
                    onChange={(e) => {
                      !isReadOnly && setDoctor(e.target.value)
                      setErrors({ ...errors, doctor: false })
                    }}
                    readOnly={isReadOnly}
                    style={{
                      backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                      cursor: isReadOnly ? 'not-allowed' : 'text',
                    }}
                    required
                  />
                </div>
                <div>
                  <label className='form-label'>
                    <StyledLabel>
                      <FaHospital />
                      Poli <span style={{ color: '#059BB6' }}> *</span>
                    </StyledLabel>
                  </label>
                  <input
                    type='text'
                    className={`form-control ${errors.department ? 'border-red-500' : ''}`}
                    value={department}
                    onChange={(e) => {
                      !isReadOnly && setDepartment(e.target.value)
                      setErrors({ ...errors, department: false })
                    }}
                    readOnly={isReadOnly}
                    style={{
                      backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                      cursor: isReadOnly ? 'not-allowed' : 'text',
                    }}
                    required
                  />
                </div>
              </div>
            </StyledSection>
          </form>
        </div>

        {/* Section 2: Upload Documents */}
        <div className='bg-white rounded-lg' style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '16px' }}>
          <h3 className='text-lg font-semibold mb-4' style={{ color: '#059BB6', marginBottom: '16px' }}>
            Upload Documents <span style={{ color: '#059BB6' }}> *</span>
          </h3>

          {successMessage && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: '#d4edda',
                color: '#155724',
                borderRadius: '6px',
                border: '1px solid #c3e6cb',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <FaCheckCircle style={{ fontSize: '16px' }} />
              <span>{successMessage}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {renderDocuments.map((doc) => {
              const currentDoc = documentsData.find((d) => d.label === doc.label)?.document ?? []
              const isPreClaim =
                claimDetails?.status === 'Pre-Registered' ||
                claimDetails?.status === 'Pre-Auth'
              const isPendingFGL = claimDetails?.status === 'Pending on FGL'
              const hasRequestDocument =
                currentDoc && currentDoc.some((d) => d.status === 'Request Document' || d.status === 'INVALID')

              const canUploadDoc =
                !isReadOnly &&
                claimDetails?.status !== 'FGL Released' &&
                (
                  isPreClaim ||
                  (isPendingFGL && hasRequestDocument)
                )

              return (
                <div
                  key={doc.label}
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    overflow: 'hidden',
                    border: '1px solid #e9ecef',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Document Header */}
                  <div
                    style={{
                      padding: '10px 12px',
                      background: '#ffffff',
                      borderBottom: '1px solid #f1f3f5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    {getDocumentStatusIcon(currentDoc?.[0])}
                    <span style={{ fontWeight: 500, color: '#2c3e50', fontSize: '13px', flex: 1 }}>
                      {doc.label}
                    </span>

                    {canUploadDoc && (
                      <button
                        onClick={() => {
                          const fileInput = document.getElementById(`upload-${doc.label}`)
                          if (fileInput) fileInput.click()
                        }}
                        style={{
                          border: 'none',
                          background: 'rgba(5, 155, 182, 0.1)',
                          color: '#059BB6',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          marginLeft: 'auto',
                        }}
                      >
                        {isPendingFGL ? 'Reupload' : 'Upload'}
                      </button>
                    )}
                  </div>

                  {/* Upload Progress */}
                  {isUploading[doc.label] && (
                    <div style={{ padding: '12px', background: 'white', borderBottom: '1px solid #f1f3f5' }}>
                      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ display: 'flex', alignItems: 'center', color: '#059BB6', fontWeight: 500, fontSize: '13px' }}>
                          <Spinner animation='border' size='sm' style={{ marginRight: '8px', color: '#059BB6', width: '16px', height: '16px' }} />
                          Uploading...
                        </span>
                        <span style={{ background: '#059BB6', color: 'white', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 500 }}>
                          {uploadProgress}%
                        </span>
                      </div>
                      <div style={{ background: '#f0f0f0', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                        <ProgressBar $progress={uploadProgress} />
                      </div>
                    </div>
                  )}

                  {/* File List */}
                  {currentDoc && currentDoc.length > 0 && (
                    <div style={{ padding: '8px 12px' }}>

                      {currentDoc.map((file, fileIndex) => {

                        const isRevisionNeeded =
                          !isReadOnly &&
                          claimDetails?.status !== 'FGL Released' &&
                          (
                            // Case 1: Pre-Registered → semua IDLE boleh delete
                            (claimDetails?.status === 'Pre-Registered' && file.status === 'IDLE') ||

                            // Case 2: Pending on FGL → HANYA file INVALID
                            (claimDetails?.status === 'Pending on FGL' && file.status === 'IDLE') && hasRequestDocument
                          )

                        return (
                          <div
                            key={file.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px',
                              borderRadius: '4px',
                              background: '#f8f9fa',
                              marginBottom: '4px',
                              gap: '8px',
                            }}
                          >
                            <span style={{ fontSize: '12px', fontWeight: 500, color: '#2c3e50', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </span>
                            <span
                              style={{
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: 500,
                                background:
                                  file.status === 'VALID'
                                    ? 'rgba(32, 201, 151, 0.1)'
                                    : file.status === 'INVALID'
                                      ? 'rgba(250, 82, 82, 0.1)'
                                      : 'rgba(253, 126, 20, 0.1)',
                                color:
                                  file.status === 'VALID'
                                    ? '#20C997'
                                    : file.status === 'INVALID'
                                      ? '#FA5252'
                                      : file.status === 'Request Document'
                                        ? '#FA5252'
                                        : '#FD7E14',
                              }}
                            >
                              {getDocumentStatusText(file)}
                            </span>
                            {isRevisionNeeded && (
                              <button
                                onClick={() => handleDeleteFile(doc.key || 0, fileIndex)}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  padding: '0 4px',
                                }}
                              >
                                <FaTimes />
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Hidden File Input */}
                  <input
                    id={`upload-${doc.label}`}
                    type='file'
                    accept='.pdf,.jpg,.jpeg,.png,.doc,.docx'
                    style={{ display: 'none' }}
                    onChange={(e) => handleOutpatientFileChange(doc.label, e)}
                    disabled={isReadOnly}
                    multiple
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Section 3: Other Information */}
        <div className='bg-white rounded-lg' style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '16px' }}>
          <h3 className='text-lg font-semibold mb-4' style={{ color: '#059BB6', marginBottom: '16px' }}>Other Information</h3>
          <form>
            <div className='mb-2'>
              <label className='form-label'>
                <StyledLabel>
                  <FaNotesMedical />
                  Catatan
                </StyledLabel>
              </label>
              <textarea
                className='form-control'
                value={notes}
                onChange={(e) => !isReadOnly && setNotes(e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { backgroundColor: '#f5f5f5' } : {}}
              />
            </div>

            <div className='mb-2'>
              <label className='form-label'>
                <StyledLabel>
                  <FaNotesMedical />
                  Ketentuan Khusus
                </StyledLabel>
              </label>
              <div
                dangerouslySetInnerHTML={{
                  __html: parsedJson?.Condition || '',
                }}
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'not-allowed',
                  padding: '12px',
                  minHeight: '120px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  overflowY: 'auto',
                }}
              />
            </div>

            {(claimDetails?.status === 'Void' || claimDetails?.status === 'Reject') && claimDetails?.remarkRejectVoid && (
              <div className='mb-4'>
                <label className='form-label'>Remark Reject Void</label>
                <textarea
                  className='form-control'
                  value={claimDetails?.remarkRejectVoid}
                  readOnly={isReadOnly}
                  disabled
                />
              </div>
            )}

            {/* {claimDetails?.urlRegisterForm && (
              <div className='info-item full-width card-layout'>
                <div className='info-label' style={{ marginBottom: '5px' }}>
                  <label>Dokumen Registrasi</label>
                </div>
                <div className='info-value'>
                  <div
                    className='card-content'
                    style={{
                      padding: '16px',
                      border: '1px solid #dbdfe9',
                      borderRadius: '8px',
                      backgroundColor: '#f1f1f2',
                      cursor: 'pointer',
                      color: '#059BB6',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowDocModal(true)}
                  >
                    Lihat Dokumen Registrasi
                  </div>
                </div>
              </div>
            )} */}
          </form>
        </div>
      </div>
    </>
  )
})

export default OutpatientPage
